import React, { useCallback, useEffect, useRef, useState } from 'react';

import { getCurrencySymbolFromCode } from '@futrli/localisation';

import { FreestyleContentForm } from '../freestyle-content-form/freestyle-content-form';
import { FreestyleMethodCellData, FreestyleMethodRowData } from '../freestyle-content-form/types';
import { QuickActionsType } from '../types';
import * as utils from './utils';

interface FreestyleContentProps {
  currency?: string;
  format?: 'currency' | 'decimal';
  isAddingNew?: boolean;
  isDisabled?: boolean;
  isDisabledAddYear?: boolean;
  onAddFinancialYear?: () => void;
  onChange?: (newRows: FreestyleMethodRowData[]) => void;
  onStoreOnTheChange?: React.ChangeEventHandler<HTMLInputElement>;
  rows?: FreestyleMethodRowData[];
  reset?: number;
  storeOnThe?: string;
  yearsAdded?: string[];
}

export const FreestyleContent = ({
  currency,
  format,
  isAddingNew,
  isDisabled,
  isDisabledAddYear,
  onAddFinancialYear,
  onChange,
  onStoreOnTheChange,
  rows = [],
  reset,
  storeOnThe,
  yearsAdded
}: FreestyleContentProps) => {
  const currencySymbol = currency ? getCurrencySymbolFromCode(currency) : undefined;
  const [quickActionsType, setQuickActionsType] = useState(QuickActionsType.setSelectedTo);
  const [quickActionsValue, setQuickActionsValue] = useState('');
  const rowsRef = useRef<FreestyleMethodRowData[]>([]);

  const isDisabledUpdate =
    !rows ||
    !quickActionsValue ||
    isDisabled ||
    !utils.isAnythingSelected(rows) ||
    // For 'set selected to' and 'split between selected' we don't need initial values
    // otherwise we need to make sure at least one selected date has a value
    (quickActionsType !== QuickActionsType.setSelectedTo &&
      quickActionsType !== QuickActionsType.splitBetweenSelected &&
      !utils.isValueDataAvailable(rows));

  const handleQuickActionsTypeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setQuickActionsType(e.target.value as QuickActionsType);
  }, []);

  const handleQuickActionsUpdate = useCallback(() => {
    const rowData = rowsRef.current;

    if (!rowData) {
      return;
    }

    let newData: FreestyleMethodRowData[] | null = null;
    switch (quickActionsType) {
      case QuickActionsType.setSelectedTo: {
        newData = utils.quickActionsSetSelectedTo(rowData, quickActionsValue);
        break;
      }
      case QuickActionsType.increaseByPercentage: {
        newData = utils.quickActionsChangeByPercentage(rowData, quickActionsValue, 'increase');
        break;
      }
      case QuickActionsType.decreaseByPercentage: {
        newData = utils.quickActionsChangeByPercentage(rowData, quickActionsValue, 'decrease');
        break;
      }
      case QuickActionsType.increaseByAmount: {
        newData = utils.quickActionsChangeByAmount(rowData, quickActionsValue, 'increase');
        break;
      }
      case QuickActionsType.decreaseByAmount: {
        newData = utils.quickActionsChangeByAmount(rowData, quickActionsValue, 'decrease');
        break;
      }
      case QuickActionsType.splitBetweenSelected: {
        newData = utils.quickActionsSplitBetweenSelected(rowData, quickActionsValue);
        break;
      }
      default:
      // we'll only get here if we dont recognise the value of quickActionsType.
    }

    if (newData) {
      onChange && onChange(newData);
    }
  }, [onChange, quickActionsType, quickActionsValue]);

  const handleQuickActionsValueChange = useCallback((value: string) => {
    setQuickActionsValue(value);
  }, []);

  const handleSelectAll = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, selectedYear: FreestyleMethodRowData) => {
      const rowData = rowsRef.current;
      const newData = utils.updateCellDataValues(rowData, selectedYear.months, selectedYear, month => ({
        ...month,
        selected: !month.readOnly ? e.target.checked : month.selected
      }));

      utils.updateIsAllSelectedValue(newData.find(year => year.date === selectedYear.date)!);
      onChange?.(newData);
    },
    [onChange]
  );

  const handleSelect = useCallback(
    (selectedMonths: FreestyleMethodCellData[], selectedYear: FreestyleMethodRowData) => {
      const rowData = rowsRef.current;
      const newData = utils.updateCellDataValues(rowData, selectedMonths, selectedYear, month => ({
        ...month,
        selected: !month.selected
      }));

      utils.updateIsAllSelectedValue(newData.find(year => year.date === selectedYear.date)!);
      onChange?.(newData);
    },
    [onChange]
  );

  const handleValueChange = useCallback(
    (newValue: string, selectedMonth: FreestyleMethodCellData, selectedYear: FreestyleMethodRowData) => {
      const rowData = rowsRef.current;
      const newData = utils.updateCellDataValues(rowData, [selectedMonth], selectedYear, month => ({
        ...month,
        value: newValue
      }));

      onChange?.(newData);
    },
    [onChange]
  );

  // Reset the quick actions dropdown / value if reset has been flagged
  useEffect(() => {
    setQuickActionsType(QuickActionsType.setSelectedTo);
    setQuickActionsValue('');
  }, [reset]);

  // Whenever the row data changes, update the ref to it (ref used for performance!)
  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  return (
    <FreestyleContentForm
      currencySymbol={currencySymbol}
      format={format}
      isAddingNew={isAddingNew}
      isDisabled={isDisabled}
      isDisabledAddYear={isDisabledAddYear}
      isDisabledUpdate={isDisabledUpdate}
      onAddFinancialYear={onAddFinancialYear}
      onQuickActionsTypeChange={handleQuickActionsTypeChange}
      onQuickActionsUpdate={handleQuickActionsUpdate}
      onQuickActionsValueChange={handleQuickActionsValueChange}
      onSelectAll={handleSelectAll}
      onSelect={handleSelect}
      onStoreOnTheChange={onStoreOnTheChange}
      onValueChange={handleValueChange}
      quickActionsType={quickActionsType}
      quickActionsValue={quickActionsValue}
      rows={rows}
      storeOnThe={storeOnThe}
      yearsAdded={yearsAdded}
    />
  );
};
